import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'oidc-client-ts';
import { userManager } from '../services/auth/oidcConfig';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      try {
        const currentUser = await userManager.getUser();
        if (currentUser && !currentUser.expired) {
          setUser(currentUser);
        }
      } catch (err) {
        console.error('Error checking user:', err);
        setError('Failed to check authentication status');
      } finally {
        setLoading(false);
      }
    };

    checkUser();

    // Listen for user changes
    const handleUserLoaded = (user: User) => {
      setUser(user);
      setError(null);
    };

    const handleUserUnloaded = () => {
      setUser(null);
    };

    const handleSilentRenewError = () => {
      console.error('Silent renew error');
      setError('Session renewal failed');
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, []);

  const login = async () => {
    try {
      setError(null);
      await userManager.signinRedirect();
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to initiate login');
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await userManager.signoutRedirect();
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout');
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user && !user.expired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};